import React from 'react';

// Define Card props interface
export interface CardProps {
  /** Custom className for the card container */
  className?: string;
  /** Whether the card has a hover effect */
  hoverable?: boolean;
  /** Whether the card has a border */
  bordered?: boolean;
  /** Custom styles for the card */
  style?: React.CSSProperties;
  /** Card children */
  children: React.ReactNode;
}

// Define CardHeader props interface
export interface CardHeaderProps {
  /** Custom className for the header */
  className?: string;
  /** Header children */
  children: React.ReactNode;
}

// Define CardBody props interface
export interface CardBodyProps {
  /** Custom className for the body */
  className?: string;
  /** Body children */
  children: React.ReactNode;
}

// Define CardFooter props interface
export interface CardFooterProps {
  /** Custom className for the footer */
  className?: string;
  /** Footer children */
  children: React.ReactNode;
}

/**
 * Card component that provides a container with optional header, body, and footer
 */
const Card: React.FC<CardProps> & {
  Header: React.FC<CardHeaderProps>;
  Body: React.FC<CardBodyProps>;
  Footer: React.FC<CardFooterProps>;
} = ({
  className = '',
  hoverable = false,
  bordered = false,
  style,
  children,
}) => {
  const baseClasses = 'bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden';
  const hoverClasses = hoverable ? 'transition-shadow hover:shadow-lg' : '';
  const borderClasses = bordered ? 'border border-gray-200 dark:border-gray-700' : '';
  
  const combinedClasses = [
    baseClasses,
    hoverClasses,
    borderClasses,
    className,
  ].join(' ');

  return (
    <div className={combinedClasses} style={style}>
      {children}
    </div>
  );
};

/**
 * Card Header component for title and actions
 */
const CardHeader: React.FC<CardHeaderProps> = ({
  className = '',
  children,
}) => {
  return (
    <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-700 ${className}`}>
      {children}
    </div>
  );
};

/**
 * Card Body component for main content
 */
const CardBody: React.FC<CardBodyProps> = ({
  className = '',
  children,
}) => {
  return (
    <div className={`p-4 ${className}`}>
      {children}
    </div>
  );
};

/**
 * Card Footer component for actions
 */
const CardFooter: React.FC<CardFooterProps> = ({
  className = '',
  children,
}) => {
  return (
    <div className={`px-4 py-3 border-t border-gray-200 dark:border-gray-700 ${className}`}>
      {children}
    </div>
  );
};

// Assign subcomponents to Card
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card; 